import { isResource } from '@medplum/core'
import type {
  Address,
  Coding,
  ContactPoint,
  Organization,
  Patient,
  Reference,
  Task,
} from '@medplum/fhirtypes'
import {
  addMonths,
  differenceInYears,
  format,
  isAfter,
  parseISO,
  startOfDay,
  startOfMonth,
} from 'date-fns'
import { compact, orderBy, uniq } from 'lodash-es'
import { stringSimilarity } from 'string-similarity-js'
import type { SeenMedplumClient } from '#src/client.ts'
import { codeableConcept } from '#src/codeableConcept.ts'
import {
  MEDICAID_ID_SYSTEM,
  MEDICARE_ID_SYSTEM,
  METRIPORT_ID_SYSTEM,
  PARTICIPANT_FLAG_SYSTEM,
  PARTICIPANT_LABEL_TAG_SYSTEM,
  SSN_SYSTEM,
  TWENTY_LEAD_ID_SYSTEM,
} from '#src/codeSystems.ts'
import { SEEN_MRN_SYSTEM } from '#src/constants.ts'
import { toDateString } from '#src/dates.ts'
import { Extension, Tag } from '#src/decorators.ts'
import type { ExportFields } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import { ParticipantLabel } from '#src/models/participantLabel.ts'
import {
  DEMO_TAG,
  DISENROLLED_TAG,
  DISENROLLMENT_SCHEDULED_TAG,
  type DisenrollmentReason,
  LEAD_TAG,
  ONSITE_TAG,
} from '#src/models/patients/constants.ts'
import {
  PARTICIPANT_FLAGS,
  ParticipantFlagCode,
  type ParticipantFlagDefinition,
} from '#src/models/patients/flags.ts'
import { homeAddressOrFallback } from '#src/models/patients/patients.ts'
import { ExtendedProfile } from '#src/models/profile.ts'
import type { QueryTypes } from '#src/search.ts'
import {
  DISENROLLMENT_REASON_SYSTEM,
  MEDICATION_DELIVERY_PREFERENCE_SYSTEM,
  MEDICATION_DELIVERY_PREFERENCE_UPDATED_AT_SYSTEM,
  METRIPORT_AI_SUMMARY_LAST_REFRESHED_SYSTEM,
  METRIPORT_AI_SUMMARY_SYSTEM,
} from '#src/structureDefinitions.ts'
import { hasTag } from '#src/tags.ts'
import { identifier, isOfType } from '#src/utils.ts'

export const MedicationDeliveryPreference = {
  Center: 'center',
  Home: 'home',
} as const

export type MedicationDeliveryPreference =
  (typeof MedicationDeliveryPreference)[keyof typeof MedicationDeliveryPreference]

export class Participant extends ExtendedProfile<Patient> {
  static readonly RESOURCE_TYPE = 'Patient'

  static isInstance(value: unknown): value is Patient {
    return isResource(value) && isOfType(value, 'Patient')
  }

  get seenMRN(): string | undefined {
    return this.identifier(SEEN_MRN_SYSTEM)?.value
  }

  set seenMRN(value: string | undefined) {
    if (value) {
      this.setIdentifier(SEEN_MRN_SYSTEM, value, {
        type: {
          coding: [
            {
              system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
              code: 'MR',
            },
          ],
        },
      })
    } else {
      this.removeIdentifier(SEEN_MRN_SYSTEM)
    }
  }
  get metriportId(): string | undefined {
    return this.identifier(METRIPORT_ID_SYSTEM)?.value
  }

  set metriportId(value: string | undefined) {
    if (value) {
      this.setIdentifier(METRIPORT_ID_SYSTEM, value)
    } else {
      this.removeIdentifier(METRIPORT_ID_SYSTEM)
    }
  }

  get twentyLeadId(): string | undefined {
    return this.identifier(TWENTY_LEAD_ID_SYSTEM)?.value
  }

  set twentyLeadId(value: string | undefined) {
    if (value) {
      this.setIdentifier(TWENTY_LEAD_ID_SYSTEM, value)
    } else {
      this.removeIdentifier(TWENTY_LEAD_ID_SYSTEM)
    }
  }

  get crmUrl(): string | undefined {
    const twentyLeadId = this.identifier(TWENTY_LEAD_ID_SYSTEM)?.value
    if (!twentyLeadId) {
      return undefined
    }
    return `${TWENTY_LEAD_ID_SYSTEM}/${twentyLeadId}`
  }

  get medicareId(): string | undefined {
    return this.identifier(MEDICARE_ID_SYSTEM)?.value
  }

  set medicareId(value: string | undefined) {
    if (value) {
      this.setIdentifier(MEDICARE_ID_SYSTEM, value, {
        type: {
          coding: [
            {
              system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
              code: 'MC',
            },
          ],
        },
      })
    } else {
      this.removeIdentifier(MEDICARE_ID_SYSTEM)
    }
  }

  get medicaidId(): string | undefined {
    return this.identifier(MEDICAID_ID_SYSTEM)?.value
  }

  set medicaidId(value: string | undefined) {
    if (value) {
      this.setIdentifier(MEDICAID_ID_SYSTEM, value, {
        type: {
          coding: [
            {
              system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
              code: 'MA',
            },
          ],
        },
      })
    } else {
      this.removeIdentifier(MEDICAID_ID_SYSTEM)
    }
  }

  get ssn(): string | undefined {
    return this.identifier(SSN_SYSTEM)?.value
  }

  set ssn(value: string | undefined) {
    if (value) {
      this.setIdentifier(SSN_SYSTEM, value, {
        type: {
          coding: [
            {
              system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
              code: 'SS',
            },
          ],
        },
      })
    } else {
      this.removeIdentifier(SSN_SYSTEM)
    }
  }

  /*
   * Patients with this Seen MRN. Merge losers can still carry the MRN, so
   * DELETED records are excluded unless `includeDeleted` is set; there may
   * still be several matches, so prefer `findBySeenMRN` for a single one.
   */
  static bySeenMRN(
    seenMRN: string,
    { includeDeleted = false }: { includeDeleted?: boolean } = {}
  ): QueryTypes {
    const query: QueryTypes = {}
    if (!includeDeleted) {
      query['_tag:not'] = ExtendedProfile.DELETED_TAG
    }
    query.identifier = identifier(SEEN_MRN_SYSTEM, seenMRN)
    return query
  }

  static async findBySeenMRN(
    medplum: SeenMedplumClient,
    seenMRN: string
  ): Promise<Participant | undefined> {
    const matches = await medplum.searchResourceClassPagesAll(
      Participant,
      Participant.bySeenMRN(seenMRN)
    )
    return Participant.resolveFromDuplicates(matches)
  }

  static byTwentyLeadId(twentyLeadId: string): QueryTypes {
    return {
      ...Participant.baseSearchQuery(),
      identifier: identifier(TWENTY_LEAD_ID_SYSTEM, twentyLeadId),
    }
  }

  static byName(name: string): QueryTypes {
    return {
      // Strip commas, as the server will treat them as OR conditions
      name: name.replace(/,/g, ''),
    }
  }

  static byNameAndDOB(name: string, dob: string): QueryTypes {
    return {
      ...this.byName(name),
      birthdate: dob,
    }
  }

  @Tag({
    system: PARTICIPANT_FLAG_SYSTEM,
    allowMultiple: true,
  })
  _flags?: Coding[]

  get flags(): ParticipantFlagDefinition[] {
    const flags = compact(
      this._flags?.map((f) => PARTICIPANT_FLAGS[f.code as ParticipantFlagCode])
    )
    return orderBy(flags, ['priority'], ['asc'])
  }

  set flags(flags: ParticipantFlagCode[]) {
    if (flags.length === 0) {
      this._flags = undefined
      return
    }
    const flagDefinitions = flags.map((f) => PARTICIPANT_FLAGS[f])
    const orderedFlags = orderBy(flagDefinitions, ['priority'], ['asc'])
    this._flags = compact(orderedFlags.map((f) => f.coding))
  }

  get flagCodes(): ParticipantFlagCode[] {
    return compact(
      this.flags.map((flag) => flag.coding.code as ParticipantFlagCode)
    )
  }

  @Tag({
    system: PARTICIPANT_LABEL_TAG_SYSTEM,
    allowMultiple: true,
  })
  _labels?: Coding[]

  get labels(): ParticipantLabel['referenceString'][] {
    return compact(
      this._labels
        ?.map((l) => l.code)
        .filter((c) => ParticipantLabel.isValidReferenceString(c))
    )
  }

  set labels(labels: ParticipantLabel['referenceString'][]) {
    if (labels.length === 0) {
      this._labels = undefined
      return
    }
    this._labels = labels.map((l) => ({
      code: l,
      system: PARTICIPANT_LABEL_TAG_SYSTEM,
    }))
  }

  /**
   * Base FHIR search query for this resource — extended/modified at call sites.
   * Changing this affects every caller. Grep `baseSearchQuery` for impact before editing.
   */
  static baseSearchQuery(): QueryTypes {
    return {
      '_tag:not': ExtendedProfile.DELETED_TAG,
      _sort: ['family', '-_lastUpdated'],
      _count: '1000',
    }
  }

  static enrolledSearchQuery(): QueryTypes {
    return {
      ...Participant.baseSearchQuery(),
      active: true,
    }
  }

  static demoOnlySearchQuery(): QueryTypes {
    return {
      ...Participant.enrolledSearchQuery(),
      _tag: DEMO_TAG,
    }
  }

  /**
   * Given multiple Patient records for the same person (e.g. duplicate MRNs),
   * resolve the best one: prefers active + non-replaced, falls back to
   * non-replaced inactive. Returns undefined if all are replaced.
   *
   * Throws if participants have mismatched Seen MRNs.
   */
  static resolveFromDuplicates(
    participants: Participant[]
  ): Participant | undefined {
    const mrns = uniq(compact(participants.map((p) => p.seenMRN)))
    invariant(
      mrns.length <= 1,
      `Cannot resolve from duplicates with mismatched MRNs: ${mrns.join(', ')}`
    )

    return (
      participants.find((p) => p.isActive && !p.replacedBy) ??
      participants.find((p) => !p.replacedBy)
    )
  }

  get isActive(): boolean {
    return this.resource.active ?? false
  }

  set isActive(value: boolean) {
    this.resource.active = value
  }

  get replacedBy(): Reference<Patient> | undefined {
    return this.resource.link?.find(
      (l) => l.type === 'replaced-by' && isOfType(l.other, 'Patient')
    )?.other as Reference<Patient> | undefined
  }

  set replacedBy(value: Reference<Patient> | undefined) {
    this.resource.link =
      this.resource.link?.filter((l) => l.type !== 'replaced-by') ?? []
    if (value) {
      this.delete()
      this.resource.link?.push({
        type: 'replaced-by',
        other: value,
      })
      this.isActive = false
    }
  }

  get isDemo(): boolean {
    return this.hasTag(DEMO_TAG)
  }

  set isDemo(value: boolean) {
    if (value) {
      this.addTag(DEMO_TAG)
    } else {
      this.removeTag(DEMO_TAG)
    }
  }

  get isLead(): boolean {
    return this.hasTag(LEAD_TAG)
  }

  set isLead(value: boolean) {
    if (value) {
      this.addTag(LEAD_TAG)
    } else {
      this.removeTag(LEAD_TAG)
    }
  }

  get relativeUrl(): string {
    return `/Patient/${this.id}`
  }

  get isOrbitNative(): boolean {
    return this.flagCodes.includes(ParticipantFlagCode.ORBIT_NATIVE)
  }

  set isOrbitNative(value: boolean) {
    const flag = PARTICIPANT_FLAGS[ParticipantFlagCode.ORBIT_NATIVE].coding
    if (value) {
      this.addTag(flag)
    } else {
      this.removeTag(flag)
    }
  }

  get isOnsite(): boolean {
    return this.hasTag(ONSITE_TAG)
  }

  set isOnsite(value: boolean) {
    if (value) {
      this.addTag(ONSITE_TAG)
    } else {
      this.removeTag(ONSITE_TAG)
    }
  }

  @Extension({
    url: MEDICATION_DELIVERY_PREFERENCE_SYSTEM,
    kind: 'valueString',
  })
  private _medicationDeliveryPreference?: string

  get medicationDeliveryPreference(): MedicationDeliveryPreference | undefined {
    return this._medicationDeliveryPreference as
      | MedicationDeliveryPreference
      | undefined
  }

  set medicationDeliveryPreference(
    value: MedicationDeliveryPreference | null | undefined
  ) {
    const next = value ?? undefined
    if (next === this._medicationDeliveryPreference) {
      return
    }
    this._medicationDeliveryPreference = next
    this.medicationDeliveryPreferenceUpdatedAt = new Date().toISOString()
  }

  @Extension({
    url: MEDICATION_DELIVERY_PREFERENCE_UPDATED_AT_SYSTEM,
    kind: 'valueDateTime',
  })
  private _medicationDeliveryPreferenceUpdatedAt?: string

  get medicationDeliveryPreferenceUpdatedAt(): string | undefined {
    return this._medicationDeliveryPreferenceUpdatedAt
  }

  set medicationDeliveryPreferenceUpdatedAt(value: string | null | undefined) {
    this._medicationDeliveryPreferenceUpdatedAt = value ?? undefined
  }

  @Extension({
    url: METRIPORT_AI_SUMMARY_SYSTEM,
    kind: 'valueString',
  })
  private _metriportAiSummary?: string

  get metriportAiSummary(): string | undefined {
    return this._metriportAiSummary
  }

  set metriportAiSummary(value: string | undefined) {
    this._metriportAiSummary = value
  }

  @Extension({
    url: METRIPORT_AI_SUMMARY_LAST_REFRESHED_SYSTEM,
    kind: 'valueDateTime',
  })
  private _metriportAiSummaryLastRefreshed?: string

  get metriportAiSummaryLastRefreshed(): string | undefined {
    return this._metriportAiSummaryLastRefreshed
  }

  set metriportAiSummaryLastRefreshed(value: string | undefined) {
    this._metriportAiSummaryLastRefreshed = value
  }

  get managingOrganization(): Reference<Organization> | undefined {
    return this.resource.managingOrganization
  }

  set managingOrganization(org: Reference<Organization> | undefined) {
    this.resource.managingOrganization = org
  }

  get isDisenrollmentScheduled(): boolean {
    return this.hasTag(DISENROLLMENT_SCHEDULED_TAG)
  }

  set isDisenrollmentScheduled(value: boolean) {
    if (value) {
      this.addTag(DISENROLLMENT_SCHEDULED_TAG)
    } else {
      this.removeTag(DISENROLLMENT_SCHEDULED_TAG)
    }
  }

  get isDisenrolled(): boolean {
    return this.hasTag(DISENROLLED_TAG)
  }

  set isDisenrolled(value: boolean) {
    if (value) {
      this.addTag(DISENROLLED_TAG)
    } else {
      this.removeTag(DISENROLLED_TAG)
    }
  }

  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/enrollment-date',
    kind: 'valueDateTime',
  })
  private _enrollmentDate?: string

  get enrollmentDate(): Date | undefined {
    return this._enrollmentDate ? parseISO(this._enrollmentDate) : undefined
  }

  set enrollmentDate(value: Date | string | null | undefined) {
    if (!value) {
      this._enrollmentDate = undefined
      return
    }
    this._enrollmentDate = value instanceof Date ? value.toISOString() : value
  }

  get enrollmentDateString(): string | null {
    return this.enrollmentDate ? toDateString(this.enrollmentDate) : null
  }

  canWithdrawEnrollment(referenceDate: Date = new Date()): boolean {
    if (this.isLead) {
      return false
    }

    const enrollmentDate = this.enrollmentDate
    if (!enrollmentDate) {
      return false
    }

    const enrollmentDay = startOfDay(enrollmentDate)
    const referenceDay = startOfDay(referenceDate)

    return !isAfter(referenceDay, enrollmentDay)
  }

  withdrawEnrollment(): void {
    this.isLead = true
    this.isActive = false
    this.enrollmentDate = undefined
    this.disenrollmentDate = undefined
    this.isDisenrolled = false
  }

  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/disenrollment-date',
    kind: 'valueDateTime',
  })
  private _disenrollmentDate?: string

  get disenrollmentDate(): Date | undefined {
    return this._disenrollmentDate
      ? parseISO(this._disenrollmentDate)
      : undefined
  }

  set disenrollmentDate(value: Date | string | null | undefined) {
    if (!value) {
      this._disenrollmentDate = undefined
      this.isDisenrollmentScheduled = false
      return
    }
    this._disenrollmentDate =
      value instanceof Date ? value.toISOString() : value
    this.isDisenrollmentScheduled = true
  }

  @Extension({
    url: DISENROLLMENT_REASON_SYSTEM,
    kind: 'valueString',
  })
  private _disenrollmentReason?: string

  get disenrollmentReason(): DisenrollmentReason | undefined {
    return this._disenrollmentReason as DisenrollmentReason | undefined
  }

  set disenrollmentReason(value: DisenrollmentReason | null | undefined) {
    this._disenrollmentReason = value ?? undefined
  }

  static computeEnrollmentDate(signDate: Date): string {
    return toDateString(startOfMonth(addMonths(signDate, 1)))
  }

  get birthDate(): string | undefined {
    return this.resource.birthDate
  }

  set birthDate(value: string | undefined) {
    this.resource.birthDate = value
  }

  get birthDateFormatted(): string | undefined {
    return this.birthDate && format(parseISO(this.birthDate), 'MMM d yyyy')
  }

  get age(): number | undefined {
    return this.resource?.birthDate
      ? differenceInYears(new Date(), parseISO(this.resource.birthDate))
      : undefined
  }

  get contactPoints(): ContactPoint[] {
    return this.resource.telecom ?? []
  }

  get homeAddress(): Address | undefined {
    return homeAddressOrFallback(this.resource)
  }

  setPhoneNumber(
    use: ContactPoint['use'],
    phoneNumber: string | undefined
  ): void {
    if (!phoneNumber) {
      return
    }
    this.resource.telecom ??= []

    // Remove existing phone numbers with the same use
    this.resource.telecom = [
      ...this.resource.telecom.filter(
        (t) => !(t.system === 'phone' && t.use === use)
      ),
      {
        system: 'phone',
        value: phoneNumber,
        use,
      },
    ]
  }

  addContact({
    firstName,
    lastName,
    phoneNumber,
  }: {
    firstName?: string
    lastName?: string
    phoneNumber?: string
  }): void {
    this.resource.contact ??= []

    // Check if a contact with the same phone number already exists
    if (
      phoneNumber &&
      this.resource.contact.some((contact) =>
        contact.telecom?.some(
          (t) => t.system === 'phone' && t.value === phoneNumber
        )
      )
    ) {
      return
    }

    this.resource.contact.push({
      relationship: [
        codeableConcept({
          system: 'http://terminology.hl7.org/CodeSystem/v2-0131',
          code: 'CP',
          display: 'Contact person',
        }),
      ],
      name: {
        given: firstName ? [firstName] : [],
        family: lastName,
        use: 'usual',
      },
      telecom: phoneNumber
        ? [
            {
              system: 'phone',
              value: phoneNumber,
            },
          ]
        : undefined,
    })
  }

  hasFlag(flag: Coding): boolean {
    return hasTag(this.resource, flag)
  }

  protected getExportFields(): ExportFields {
    return {
      seenMRN: this.seenMRN,
      elationId: this.elationId,
      patientId: this.id,
      birthDate: this.birthDate,
      gender: this.gender,
      flagCodes: this.flagCodes,
      firstName: this.firstName,
      lastName: this.lastName,
      nickname: this.nickname,
      primaryEmail: this.primaryEmail,
      primaryPhoneNumber: this.primaryPhoneNumber,
      streetAddress: this.primaryAddress?.line,
      city: this.primaryAddress?.city,
      state: this.primaryAddress?.state,
      postalCode: this.primaryAddress?.postalCode,
      address: this.addressSummary,
      isActive: this.isActive,
      isDemo: this.isDemo,
      isLead: this.isLead,
      isOnsite: this.isOnsite,
      isDeleted: this.isDeleted,
      enrollmentDate: this.enrollmentDate,
      disenrollmentDate: this.disenrollmentDate,
      deceased: this.deceased,
      deceasedDateTime: this.deceasedDateTime,
      languages: this.languageCodes,
      preferredLanguageDisplay: this.preferredLanguageDisplay,
      nextTask: this.nextTask?.reference,
      labels: this.labels,
    }
  }

  get deceased(): boolean {
    return this.resource.deceasedBoolean ?? false
  }

  get deceasedDateTime(): string | undefined {
    return this.resource.deceasedDateTime
  }

  set deceasedDateTime(value: string | null | undefined) {
    if (value) {
      this.resource.deceasedDateTime = value
      delete this.resource.deceasedBoolean
    } else {
      delete this.resource.deceasedDateTime
    }
  }

  get searchKeywords(): string[] {
    const names = [this.name, this.nickname, this.firstName, this.lastName]
    const telecoms = this.contactPoints.map((t) => t.value)
    const dobs = this.birthDate
      ? [
          this.birthDate,
          this.birthDateFormatted,
          format(parseISO(this.birthDate), 'yyyy/MM/dd'),
          format(parseISO(this.birthDate), 'MM/dd/yyyy'),
        ]
      : []
    return compact([
      ...names,
      ...telecoms,
      ...dobs,
      this.id,
      this.elationId ? String(this.elationId) : undefined,
      this.seenMRN,
      this.medicareId,
      this.medicaidId,
    ])
  }

  get searchableText(): string {
    return this.searchKeywords.join(' ').toLowerCase()
  }

  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/next-task',
    kind: 'valueReference',
  })
  private _nextTask?: Reference<Task>

  get nextTask(): Reference<Task> | undefined {
    return this._nextTask
  }

  set nextTask(taskReference: Reference<Task> | undefined) {
    this._nextTask = taskReference
  }

  // Returns a number between 0 and 1 representing the similarity between this participant and another
  similarity(other: Participant): number {
    const weights = {
      name: 0.4,
      phone: 0.1,
      email: 0.1,
      dob: 0.25,
      address: 0.1,
    }

    let totalWeight = 0
    let weightedSum = 0

    const nameScore = stringSimilarity(this.name, other.name)
    weightedSum += nameScore * weights.name
    totalWeight += weights.name

    const thisPrimaryPhoneNumber = this.primaryPhoneNumber ?? ''
    const otherPrimaryPhoneNumber = other.primaryPhoneNumber ?? ''
    if (thisPrimaryPhoneNumber && otherPrimaryPhoneNumber) {
      const phoneScore = stringSimilarity(
        thisPrimaryPhoneNumber,
        otherPrimaryPhoneNumber
      )
      weightedSum += phoneScore * weights.phone
      totalWeight += weights.phone
    }

    const thisPrimaryEmail = this.primaryEmail ?? ''
    const otherPrimaryEmail = other.primaryEmail ?? ''
    if (thisPrimaryEmail && otherPrimaryEmail) {
      const emailScore = stringSimilarity(thisPrimaryEmail, otherPrimaryEmail)
      weightedSum += emailScore * weights.email
      totalWeight += weights.email
    }

    const thisDob = this.birthDate ?? ''
    const otherDob = other.birthDate ?? ''
    if (thisDob && otherDob) {
      const dobScore = stringSimilarity(thisDob, otherDob)
      weightedSum += dobScore * weights.dob
      totalWeight += weights.dob
    }

    const thisAddress = this.primaryAddress?.line?.join(' ') ?? ''
    const otherAddress = other.primaryAddress?.line?.join(' ') ?? ''
    if (thisAddress && otherAddress) {
      const addressScore = stringSimilarity(thisAddress, otherAddress)
      weightedSum += addressScore * weights.address
      totalWeight += weights.address
    }

    return totalWeight > 0 ? weightedSum / totalWeight : 0
  }
}

export type ParticipantReferenceString = string
